// src/components/layout/SideBar.tsx
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { NavLink } from 'react-router-dom';
import './SideBar.scss';
import cn from 'classnames'; // Use classnames library
import { Home, TvMinimal, Building, ArrowLeftToLine, ArrowRight, ChevronDown, MessageSquareText, User, Users, Globe, Activity, MessageCircle, Edit, Monitor, Zap, FlaskConical, ClipboardList, ClipboardCheck, Package, BarChart2, Trophy, Star, Inbox, FileBarChart, UserPlus, Contact, School, GraduationCap, Backpack, Award,} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import UpgradeModal from '../trial/UpgradeModal';
import { useAssignmentCount } from '../../contexts/AssignmentCountContext';
import { useCanViewJoinRequests } from '../join-requests/useCanViewJoinRequests';
import { useClassNav } from '../classes/useClassNav';


interface SideBarProps {
    isCollapsed: boolean;
    onToggle: () => void; // Function to toggle the collapse state
    isMobileOpen: boolean;
    onCloseMobile: () => void;
}

interface NavItem {
    // Translation key for the visible label (public/locales/<lang>.json → "sidebar").
    labelKey: string;
    icon: React.ReactNode;
    path: string;
    roles: string[];
    marketplaceBlocked: boolean;
    // Optional DOM id used to anchor the interactive tutorial (see components/tutorial/interactiveSteps.ts).
    tourId?: string;
}

// Nav items are grouped into labeled sections so the sidebar stays legible as the
// product grows: new features slot into the right section rather than lengthening
// one flat list. Items marked marketplaceBlocked are hidden for marketplace users
// regardless of role. A section renders only if the current role can see at least
// one of its items; when just one section is visible (e.g. trainees) the header is omitted.
// `section` stays English in every language: it keys the saved collapsed state and the
// list's DOM id. What the user reads is `labelKey`.
const navGroups: Array<{ section: string; labelKey: string; items: NavItem[] }> = [
    {
        section: 'Practice',
        labelKey: 'sidebar.practice',
        items: [
            { labelKey: 'sidebar.home', icon: <Home />, path: '/', roles: ['super_admin', 'admin', 'trainer', 'trainee'], marketplaceBlocked: false, tourId: 'tour-nav-home' },
            { labelKey: 'sidebar.rolePlay', icon: <TvMinimal />, path: '/roleplay', roles: ['super_admin', 'admin', 'trainer', 'trainee'], marketplaceBlocked: false, tourId: 'tour-nav-roleplay' },
            { labelKey: 'sidebar.myAssignments', icon: <ClipboardList />, path: '/my-assignments', roles: ['admin', 'trainer', 'trainee'], marketplaceBlocked: false, tourId: 'tour-nav-my-assignments' },
            // Student system: shown only to people who teach or study in a class (see useClassNav).
            { labelKey: 'sidebar.myPractice', icon: <Backpack />, path: '/my-practice', roles: ['admin', 'trainer', 'trainee'], marketplaceBlocked: false, tourId: 'tour-nav-my-practice' },
            { labelKey: 'sidebar.myGrades', icon: <Award />, path: '/my-grades', roles: ['admin', 'trainer', 'trainee'], marketplaceBlocked: false, tourId: 'tour-nav-my-grades' },
            { labelKey: 'sidebar.myClasses', icon: <GraduationCap />, path: '/my-classes', roles: ['admin', 'trainer', 'trainee'], marketplaceBlocked: false, tourId: 'tour-nav-my-classes' },
            { labelKey: 'sidebar.feedback', icon: <MessageSquareText />, path: '/sessions', roles: ['super_admin', 'admin', 'trainer', 'trainee'], marketplaceBlocked: false, tourId: 'tour-nav-feedback' },
            { labelKey: 'sidebar.myProgress', icon: <BarChart2 />, path: '/my-progress', roles: ['super_admin', 'admin', 'trainer', 'trainee'], marketplaceBlocked: false, tourId: 'tour-nav-my-progress' },
            { labelKey: 'sidebar.competitions', icon: <Trophy />, path: '/competitions', roles: ['super_admin', 'admin', 'trainer', 'trainee'], marketplaceBlocked: false, tourId: 'tour-nav-competitions' },
        ],
    },
    {
        section: 'Manage Training',
        labelKey: 'sidebar.manageTraining',
        items: [
            { labelKey: 'sidebar.assignments', icon: <ClipboardCheck />, path: '/assignments', roles: ['super_admin', 'admin', 'trainer'], marketplaceBlocked: false, tourId: 'tour-nav-assignments' },
            { labelKey: 'sidebar.reviews', icon: <Star />, path: '/reviews', roles: ['super_admin', 'admin', 'trainer'], marketplaceBlocked: false, tourId: 'tour-nav-reviews' },
            { labelKey: 'sidebar.myReviews', icon: <Inbox />, path: '/reviewer-dashboard', roles: ['super_admin', 'admin', 'trainer'], marketplaceBlocked: false, tourId: 'tour-nav-reviewer-dashboard' },
            { labelKey: 'sidebar.bundles', icon: <Package />, path: '/bundles', roles: ['super_admin', 'admin'], marketplaceBlocked: false, tourId: 'tour-nav-bundles' },
            { labelKey: 'sidebar.teamProgress', icon: <Users />, path: '/team-progress', roles: ['admin', 'trainer'], marketplaceBlocked: false, tourId: 'tour-nav-team-progress' },
            { labelKey: 'sidebar.users', icon: <User />, path: '/users', roles: ['super_admin', 'admin', 'trainer'], marketplaceBlocked: false, tourId: 'tour-nav-users' },
            // Sits next to Users because it is the same job: deciding who is in the org.
            // Admin always sees it; a Trainer only with approval rights (Billing Owner or the
            // per-account switch) — isVisible() hides it otherwise, since the server 403s the
            // list for a Trainer without them (see useCanViewJoinRequests). Not super admin:
            // they have no org, so the page is only a "go to Organizations" dead end — their
            // queues live on each org's detail page. "View as" admin/trainer still shows it.
            { labelKey: 'sidebar.joinRequests', icon: <UserPlus />, path: '/join-requests', roles: ['admin', 'trainer'], marketplaceBlocked: false, tourId: 'tour-nav-join-requests' },
            // Student system: an org admin sees it once their school has it on (see useClassNav).
            { labelKey: 'sidebar.classes', icon: <School />, path: '/classes', roles: ['super_admin', 'admin'], marketplaceBlocked: true, tourId: 'tour-nav-classes' },
            { labelKey: 'sidebar.dashboard', icon: <Activity />, path: '/dashboard', roles: ['super_admin', 'admin', 'trainer'], marketplaceBlocked: false, tourId: 'tour-nav-dashboard' },
        ],
    },
    {
        section: 'Setup & Admin',
        labelKey: 'sidebar.setupAdmin',
        items: [
            { labelKey: 'sidebar.characters', icon: <Contact />, path: '/characters', roles: ['super_admin', 'admin'], marketplaceBlocked: true, tourId: 'tour-nav-characters' },
            { labelKey: 'sidebar.promptStudio', icon: <Edit />, path: '/prompt-studio', roles: ['super_admin', 'admin'], marketplaceBlocked: true, tourId: 'tour-nav-prompt-studio' },
            { labelKey: 'sidebar.sceneEditor', icon: <Monitor />, path: '/scene-editor', roles: ['super_admin', 'admin'], marketplaceBlocked: true, tourId: 'tour-nav-scene-editor' },
            { labelKey: 'sidebar.organizations', icon: <Building />, path: '/organizations', roles: ['super_admin'], marketplaceBlocked: true, tourId: 'tour-nav-organizations' },
            // An admin's own org and its departments; the list above is platform-wide.
            { labelKey: 'sidebar.myOrganization', icon: <Building />, path: '/my-organization', roles: ['admin'], marketplaceBlocked: true, tourId: 'tour-nav-my-organization' },
            { labelKey: 'sidebar.platformOverview', icon: <Globe />, path: '/platform-dashboard', roles: ['super_admin'], marketplaceBlocked: true, tourId: 'tour-nav-platform-dashboard' },
            { labelKey: 'sidebar.reports', icon: <FileBarChart />, path: '/reports', roles: ['super_admin'], marketplaceBlocked: true, tourId: 'tour-nav-reports' },
            { labelKey: 'sidebar.appFeedback', icon: <MessageCircle />, path: '/feedback', roles: ['super_admin', 'admin'], marketplaceBlocked: true, tourId: 'tour-nav-app-feedback' },
        ],
    },
];

// Trial users only get the core loop: Home, Role Play, Feedback. Role Play moved
// from '/' to '/roleplay' when Home took over the root route, keep both listed.
const TRIAL_PATHS = ['/', '/roleplay', '/sessions'];

const COLLAPSED_SECTIONS_KEY = 'rc_sidebar_collapsed_sections';

function loadCollapsedSections(): Record<string, boolean> {
    try { return JSON.parse(localStorage.getItem(COLLAPSED_SECTIONS_KEY) || '{}'); }
    catch { return {}; }
}

const SideBar: React.FC<SideBarProps> = ({ isCollapsed, onToggle, isMobileOpen, onCloseMobile }) => {
    const { t } = useTranslation();
    const { isMarketplaceUser, isTrialUser, isTrialExhausted, trialSessionsUsed, trialSessionLimit, userRole: authRole, currentUser } = useAuth();
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    // Org already has a Billing Owner other than this person — self-serve upgrade isn't
    // a seated/invited teammate's call to make, so the CTA that would have them pay again
    // is hidden for them. Read directly off the org (populated for every role via
    // /user/{uid}), not off the join-request record — a user who joined the org by
    // self-signup rather than the join-request flow (e.g. the org's very first member)
    // has no JoinRequest row at all, so that signal always read "no Billing Owner" for
    // them regardless of the org's actual state.
    // The Billing Owner themself must still see this once THEIR OWN paid access has
    // expired — `is_org_billing_owner` excludes them from the suppression, otherwise an
    // expired owner has no renew path anywhere in the app (same fix as GeminiDirectStreamPage).
    // `is_org_billing_owner` is organizations.billing_owner_id === me, which is NULL for every
    // org that subscribed before that column existed; `is_billing_owner` (their own
    // stripe_customer_id) stays true for those legacy owners, so accept either.
    // `invited_by` covers the same legacy orgs from the teammate's side: has_billing_owner
    // reads false there, but someone the owner invited or seated still has nothing of their
    // own to upgrade — same definition as GeminiDirectStreamPage.
    const isBillingOwner = Boolean(currentUser?.is_org_billing_owner || currentUser?.is_billing_owner);
    const hasOrgBillingOwner =
        Boolean(currentUser?.organization?.has_billing_owner || currentUser?.invited_by) && !isBillingOwner;
    // A paid billing owner whose access_end_date has passed is not a "trial" user, so
    // isTrialUser alone would never show them the Upgrade button — isTrialExhausted also
    // covers a paid subscription's access_end_date, so include it here too.
    const showUpgradeButton = isTrialUser || (isTrialExhausted && isBillingOwner);
    // A plain Trainer with no approval rights gets a 403 from the list endpoint (see
    // list_join_requests) — no point showing a nav link that dead-ends in a redirect.
    const canViewJoinRequests = useCanViewJoinRequests();
    const classNav = useClassNav();
    const { count: assignmentCount } = useAssignmentCount();
    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(loadCollapsedSections);

    // Close the mobile drawer on Escape, matching driver.js's own overlay-dismiss convention.
    // Skipped while the upgrade modal (portaled above the drawer, see UpgradeModal.scss) is
    // open, so Escape dismisses that topmost overlay instead of silently closing the drawer
    // behind it.
    useEffect(() => {
        if (!isMobileOpen || showUpgradeModal) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onCloseMobile();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isMobileOpen, showUpgradeModal, onCloseMobile]);

    // The upgrade modal itself has no Escape handler — closing it here keeps
    // Escape acting on whichever overlay is actually on top.
    useEffect(() => {
        if (!showUpgradeModal) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setShowUpgradeModal(false);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showUpgradeModal]);

    // Use the reactive role from AuthContext so the sidebar always reflects the
    // same role as the rest of the app. localStorage.getItem('role') is only
    // updated on a fresh API fetch and can lag behind when the 30-min authCache
    // fast-path is used, causing the sidebar and profile badge to disagree.
    const userRole = authRole || localStorage.getItem('role') || 'trainee';

    const isVisible = (item: NavItem) => {
        if (!item.roles.includes(userRole)) return false;
        // Admin always sees Join Requests — only a Trainer's access
        // depends on approval rights, so the hook is only consulted for that role.
        if (item.path === '/join-requests' && userRole === 'trainer' && !canViewJoinRequests) return false;
        if (item.path === '/classes' && !classNav.showClasses) return false;
        if (item.path === '/my-classes' && !classNav.showMyClasses) return false;
        if (item.path === '/my-practice' && !classNav.showStudentHome) return false;
        if (item.path === '/my-grades' && !classNav.showStudentHome) return false;
        if (isMarketplaceUser && item.marketplaceBlocked && userRole !== 'super_admin') return false;
        if (isTrialUser && !TRIAL_PATHS.includes(item.path)) return false;
        return true;
    };

    // Only sections with at least one visible item are shown.
    const visibleGroups = navGroups
        .map(g => ({ section: g.section, labelKey: g.labelKey, items: g.items.filter(isVisible) }))
        .filter(g => g.items.length > 0);

    // With a single visible section (e.g. trainees) the header adds no value, so
    // render a flat list instead.
    const showSectionHeaders = visibleGroups.length > 1 && !isCollapsed;

    const toggleSection = (section: string) => {
        setCollapsedSections(prev => {
            const next = { ...prev, [section]: !prev[section] };
            try { localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(next)); } catch { /* storage blocked */ }
            return next;
        });
    };

    const renderItem = (item: NavItem) => {
        const label = t(item.labelKey);
        return (
        <li key={item.path} title={isCollapsed ? label : undefined}>
            <NavLink
                to={item.path || '#'}
                id={item.tourId}
                className={({ isActive }) => cn('nav-item', { 'active': isActive })}
                end={item.path === '/'}
                // .nav-text is display:none while collapsed, which drops it from the
                // accessibility tree — name the icon-only link explicitly then.
                aria-label={isCollapsed
                    ? (item.path === '/my-assignments' && assignmentCount > 0 ? `${label} (${assignmentCount})` : label)
                    : undefined}
                onClick={() => { if (isMobileOpen) onCloseMobile(); }}
            >
                <div className="nav-icon" aria-hidden="true"> {item.icon}</div>
                <span className="nav-text">{label}</span>
                {item.path === '/my-assignments' && assignmentCount > 0 && (
                    <span className="nav-badge">{assignmentCount}</span>
                )}
            </NavLink>
        </li>
        );
    };

    return (
        <>
        {/* Mouse/touch shortcut only — Escape closes the drawer for keyboard users. */}
        {isMobileOpen && <div className="sidebar-backdrop" aria-hidden="true" onClick={onCloseMobile} />}
        <aside className={cn('sidebar', { 'collapsed': isCollapsed, 'open': isMobileOpen })}>
            {/* Collapse toggle */}
            <button
                className="button-ghost collapse-button-top" // New class name
                type="button"
                onClick={onToggle}
                aria-expanded={!isCollapsed}
                title={isCollapsed ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar')}
                aria-label={isCollapsed ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar')}
            >
                {isCollapsed ? <ArrowRight aria-hidden="true" /> : <ArrowLeftToLine aria-hidden="true" />}
            </button>

            <div className="sidebar-header">
                <div className="logo-container">
                    {!isCollapsed && (
                        <img src="/assets/branding/rc-logo-light.png" alt="Reality Coach" className="logo-full" />
                    )}
                    <img src="/assets/branding/rc-logo-icon.png" alt="RC" className="logo-mini" />
                </div>
            </div>

            <nav className="sidebar-nav" aria-label={t('sidebar.mainNavigation')}>
                {visibleGroups.map((group, gi) => {
                    const isSectionCollapsed = !!collapsedSections[group.section];
                    const sectionListId = `nav-section-${group.section.replace(/\s+/g, '-').toLowerCase()}`;
                    return (
                        <div key={group.section} className="nav-section">
                            {showSectionHeaders ? (
                                <button
                                    type="button"
                                    className="nav-section-header"
                                    onClick={() => toggleSection(group.section)}
                                    aria-expanded={!isSectionCollapsed}
                                    aria-controls={sectionListId}
                                >
                                    <span>{t(group.labelKey)}</span>
                                    {/* Points down when closed (click to drop the items open),
                                        flips up when open — the standard accordion affordance. */}
                                    <ChevronDown
                                        size={15}
                                        className="nav-section-chevron"
                                        style={{ transform: isSectionCollapsed ? 'none' : 'rotate(180deg)', transition: 'transform 0.18s ease' }}
                                        aria-hidden="true"
                                    />
                                </button>
                            ) : (
                                // In icon-only (collapsed) mode, separate groups with a divider
                                // instead of a text header. No divider before the first group.
                                isCollapsed && gi > 0 && <div className="nav-section-divider" aria-hidden="true" />
                            )}
                            {/* Hidden rather than unmounted: the header's aria-controls has to
                                resolve to a real element precisely when the section is
                                collapsed, which is the only state where the relationship
                                matters. */}
                            <ul id={sectionListId} hidden={showSectionHeaders && isSectionCollapsed}>
                                {group.items.map(renderItem)}
                            </ul>
                        </div>
                    );
                })}
            </nav>

            {showUpgradeButton && !hasOrgBillingOwner && (
                <button
                    type="button"
                    className={cn('upgrade-button', { 'upgrade-button--collapsed': isCollapsed })}
                    onClick={() => setShowUpgradeModal(true)}
                    title={t('sidebar.upgradeYourPlan')}
                >
                    <Zap className="upgrade-button__icon" />
                    {!isCollapsed && <span>{t('sidebar.upgrade')}</span>}
                </button>
            )}
            {isTrialUser && (
                <div
                    className={cn('trial-badge', { 'trial-badge--collapsed': isCollapsed })}
                    title={t('sidebar.trialBadgeTooltip', { used: trialSessionsUsed, limit: trialSessionLimit })}
                >
                    <FlaskConical className="trial-badge__icon" />
                    {!isCollapsed && <span>{t('sidebar.trialBadge', { used: trialSessionsUsed, limit: trialSessionLimit })}</span>}
                </div>
            )}

        </aside>
        {showUpgradeModal && ReactDOM.createPortal(
            <UpgradeModal onClose={() => setShowUpgradeModal(false)} />,
            document.body
        )}
        </>
    );
};

export default SideBar;
